// store/slices/auth/authSlice.ts
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { extractThunkError } from '@/store/utils';
import type { RegistrationStep, UserType } from '@/types/AuthState';
import { getUserTypeFromToken } from '@/store/utils/jwtDecoder';
import { credentialVerificationThunk } from '@/store/thunks/auth/credentialVerificationThunk';
import { select2FAMethodThunk } from '@/store/thunks/auth/select2FAMethodThunk';
import { verify2faCodeThunk } from '@/store/thunks/auth/verify2faCodeThunk';
import { resend2faCodeThunk } from '@/store/thunks/auth/resend2faCodeThunk';
import { initialAuthState } from '@/types/AuthState';
import { sendEmailCodeThunk } from '@/store/thunks/auth/sendEmailCodeThunk';
import { sendPhoneCodeThunk } from '@/store/thunks/auth/sendPhoneCodeThunk';
import { verifyOtpThunk } from '@/store/thunks/auth/verifyOtpThunk';
import { resendEmailCodeThunk } from '@/store/thunks/auth/resendEmailCodeThunk';
import { confirmEmailThunk } from '@/store/thunks/auth/confirmEmailThunk';
import { confirmPhoneThunk } from '@/store/thunks/auth/confirmPhoneThunk';
import { resendPhoneCodeThunk } from '@/store/thunks/auth/resendPhoneCodeThunk';
import { resendRegistrationOtpThunk } from '@/store/thunks/auth/resendRegistrationOtpThunk';
import { verifyRecoveryTwoFactorThunk } from '@/store/thunks/auth/verifyRecoveryTwoFactorThunk';
import { sendRecoveryTwoFactorThunk } from '@/store/thunks/auth/sendRecoveryTwoFactorThunk';
import { verifyRecoveryOtpThunk } from '@/store/thunks/auth/verifyRecoveryOtpThunk';
import { forgotPasswordThunk } from '@/store/thunks/auth/forgotPasswordThunk';
import { registerPasswordThunk } from '@/store/thunks/auth/registerPasswordThunk';
import { recoverPasswordThunk } from '@/store/thunks/auth/recoverPasswordThunk';

const initialState = initialAuthState;

function clearRegTokenState(state: typeof initialState): void {
  state.loginStep = 'credentials';
  state.regToken = undefined;
  state.twoFactorMethod = undefined;
  state.twoFactorMaskedDestination = undefined;
  state.twoFactorExpiresInSeconds = undefined;
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearLoginError(state) {
      state.error = undefined;
      state.fieldErrors = {};
      state.setPasswordStage = undefined;
    },
    sessionExpired(state) {
      clearRegTokenState(state);
      state.error = 'Your session expired. Please log in again.';
    },
    setUkek(state, action: PayloadAction<string>) {
      state.ukek = action.payload;
    },
    setDecapsKey(state, action: PayloadAction<string>) {
      state.decapsKey = action.payload;
    },
    setUserId(state, action: PayloadAction<string | null>) {
      state.userId = action.payload;
    },
    setUserType(state, action: PayloadAction<UserType | null>) {
      state.userType = action.payload;
    },
    setUserName(state, action: PayloadAction<string>) {
      state.username = action.payload;
      state.userName = action.payload;
    },
    setRegistrationToken(state, action: PayloadAction<string>) {
      state.registrationToken = action.payload;
    },
    restoreRegistrationState(
      state,
      action: PayloadAction<{
        registrationStep: RegistrationStep;
        registrationToken: string;
      }>
    ) {
      state.registrationStep = action.payload.registrationStep;
      state.registrationToken = action.payload.registrationToken;
    },
    resetRegistration(state) {
      state.registrationStep = undefined;
      state.registrationToken = undefined;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(verifyOtpThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(verifyOtpThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.registrationStep = 'emailCode';
      })
      .addCase(verifyOtpThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })
      .addCase(resendRegistrationOtpThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(resendRegistrationOtpThunk.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(resendRegistrationOtpThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })
      // Credentials
      .addCase(credentialVerificationThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(credentialVerificationThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.username = action.meta.arg.username.toLowerCase();
        state.userName = action.meta.arg.username.toLowerCase();
        state.regToken = action.payload.twoFactorToken;
      })
      .addCase(credentialVerificationThunk.rejected, (state, action) => {
        // Admin-forced credential reset: not a login failure. Seed the email the
        // recovery steps read and let `useAuthCredentials` route to the reset screen.
        if (action.payload?.credentialsResetRequired === true) {
          const emailAddress = action.payload.emailAddress ?? '';
          state.status = 'idle';
          state.error = undefined;
          state.fieldErrors = {};
          state.forgotPasswordEmailAddress = emailAddress;
          state.username = emailAddress;
          state.userName = emailAddress;
          return;
        }
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })

      .addCase(sendEmailCodeThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
      })
      .addCase(sendEmailCodeThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(sendEmailCodeThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })

      .addCase(sendPhoneCodeThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
      })
      .addCase(sendPhoneCodeThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(sendPhoneCodeThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })

      .addCase(resendEmailCodeThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
      })
      .addCase(resendEmailCodeThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(resendEmailCodeThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })

      .addCase(resendPhoneCodeThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
      })
      .addCase(resendPhoneCodeThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(resendPhoneCodeThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })

      .addCase(confirmEmailThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
      })
      .addCase(confirmEmailThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.registrationStep = 'phoneCode';
      })
      .addCase(confirmEmailThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })

      .addCase(confirmPhoneThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
      })
      .addCase(confirmPhoneThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.registrationStep = 'password';
      })
      .addCase(confirmPhoneThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })
      // Select method
      .addCase(select2FAMethodThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(select2FAMethodThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMethod = action.meta.arg.method;
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(select2FAMethodThunk.rejected, (state, action) => {
        state.status = 'error';
        if ((action.payload as { expired?: boolean } | undefined)?.expired === true) {
          clearRegTokenState(state);
        }
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })

      // Forgot password
      .addCase(forgotPasswordThunk.pending, (state, action) => {
        state.status = 'loading';
        state.error = undefined;
        state.forgotPasswordEmailAddress = action.meta.arg.emailAddress;
        state.username = action.meta.arg.emailAddress;
        state.userName = action.meta.arg.emailAddress;
        state.fieldErrors = {};
      })
      .addCase(forgotPasswordThunk.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(forgotPasswordThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })

      .addCase(verifyRecoveryOtpThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(verifyRecoveryOtpThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.recoveryToken = action.payload.recoveryToken;
        state.recoveryMaskedPhone = action.payload.maskedPhone;
      })
      .addCase(verifyRecoveryOtpThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })
      .addCase(sendRecoveryTwoFactorThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(sendRecoveryTwoFactorThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(sendRecoveryTwoFactorThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })

      .addCase(verifyRecoveryTwoFactorThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(verifyRecoveryTwoFactorThunk.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(verifyRecoveryTwoFactorThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })

      // Verify code
      .addCase(verify2faCodeThunk.pending, (state) => {
        state.isSubmitting = true;
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(verify2faCodeThunk.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.status = 'authenticated';
        state.loginStep = 'authenticated';
        state.sessionToken = action.payload.sessionToken;
        const rawUserType = getUserTypeFromToken(action.payload.sessionToken);
        state.userType =
          rawUserType === 'Internal' || rawUserType === 'External' ? rawUserType : null;
      })
      .addCase(verify2faCodeThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.status = 'error';
        if ((action.payload as { expired?: boolean } | undefined)?.expired === true) {
          clearRegTokenState(state);
        }
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })

      // Resend
      .addCase(resend2faCodeThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
      })
      .addCase(resend2faCodeThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.twoFactorMaskedDestination = action.payload.maskedDestination;
        state.twoFactorExpiresInSeconds = action.payload.expiresInSeconds;
      })
      .addCase(resend2faCodeThunk.rejected, (state, action) => {
        state.status = 'error';
        if ((action.payload as { expired?: boolean } | undefined)?.expired === true) {
          clearRegTokenState(state);
        }
        const { fieldErrors, message } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
      })

      .addCase(registerPasswordThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
        state.setPasswordStage = undefined;
      })
      .addCase(registerPasswordThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.userId = action.payload.userId;
        state.ukek = action.payload.ukek;
        state.registrationStep = 'complete';
      })
      .addCase(registerPasswordThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message, stage } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
        state.setPasswordStage = stage;
      })

      .addCase(recoverPasswordThunk.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
        state.fieldErrors = {};
        state.setPasswordStage = undefined;
      })
      .addCase(recoverPasswordThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.userId = action.payload.userId;
        state.ukek = action.payload.ukek;
      })
      .addCase(recoverPasswordThunk.rejected, (state, action) => {
        state.status = 'error';
        const { fieldErrors, message, stage } = extractThunkError(action.payload);
        state.fieldErrors = fieldErrors;
        state.error = message ?? action.error.message;
        state.setPasswordStage = stage;
      });
  },
});

export const {
  clearLoginError,
  sessionExpired,
  setUkek,
  setDecapsKey,
  setUserId,
  setUserType,
  setUserName,
  setRegistrationToken,
  restoreRegistrationState,
  resetRegistration,
} = authSlice.actions;
export default authSlice.reducer;
